import React, { useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Spinner from "Common/Globle/Spinner";
import EditParkingBookingForm from "./Componates/EditParkingBookingForm";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import useFetchAllData from "Hooks/useFetchAllData";
import useFetchAllDataByID from "Hooks/useFetchAllDataById";
import useFetchFormDataById from "Hooks/useFetchFormDataById";
import feilds from "../json/BookingFeilds.json";
import { validateForm } from "utils/validation";
import { ParkingBookingRules } from "validation/ValidationRules";
import {
  formatDateForInput,
  calculateCheckoutDate,
  extractHoursFromDuration,
  calculateDurationInHours
} from "../Helper";
import {
  Update,
  GetAllBookingNumber,
  GetAllVehicleType,
  GetZoneTypeById,
  GetSlotTimeById,
  GetSlotTypeById,
  GetSettingById,
  GetById
} from "adminApi/ParkingBookingApi/ParkingBooking";

const EditParkingBooking = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { formdata, otherdata, FIELD_DEPENDENCIES } = feilds;

  /* -------------------- INITIAL VALUES -------------------- */
  const allFields = useMemo(() => [...formdata, ...otherdata], [formdata, otherdata]);

  const initialValues = useMemo(
    () => allFields.reduce((acc, field) => ({ ...acc, [field.name]: "" }), {}),
    [allFields]
  );

  const { values, errors, handleChange, setErrors, setValues } = useFormChange(
    initialValues,
    ParkingBookingRules
  );

  /* -------------------- FETCH BY ID -------------------- */
  const { loading: loadingFetch } = useFetchFormDataById(
    id,
    allFields,
    GetById,
    setValues
  );

  // Once fetch completes, snapshot the original slot IDs so edit form can allow re-selecting them
  useEffect(() => {
    if (!loadingFetch && values.slot_list_id) {
      setValues(prev => ({
        ...prev,
        Old_slot_id: values.slot_list_id
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingFetch]);

  /* -------------------- MASTER DATA -------------------- */
  const { data: BookingNumberOptions, loading: loadingBookingNumber } =
    useFetchAllData(GetAllBookingNumber);

  const { data: VehicleOptions, loading: loadingVehicle } =
    useFetchAllData(GetAllVehicleType);

  const { data: ZoneTypeOptions, loading: loadingZone } =
    useFetchAllDataByID(GetZoneTypeById, values.vehicle_list_id || null);

  const { data: SettingOptions, loading: loadingSetting } =
    useFetchAllDataByID(GetSettingById, values.setting_id || null);

  const slotTypeParams = useMemo(() => {
    if (!values.zone_list_id || !values.checkin_date || !values.checkout_date) return null;
    return {
      zone_list_id: values.zone_list_id,
      check_in: values.checkin_date,
      check_out: values.checkout_date
    };
  }, [values.zone_list_id, values.checkin_date, values.checkout_date]);

  const { data: SlotTypeOptions, loading: loadingSlotType } =
    useFetchAllDataByID(GetSlotTypeById, slotTypeParams);

  const slotTimeParams = useMemo(() => {
    if (!values.zone_list_id || !values.vehicle_list_id) return null;
    return {
      zone_id: values.zone_list_id,
      vehicle_id: values.vehicle_list_id
    };
  }, [values.zone_list_id, values.vehicle_list_id]);

  const { data: SlotTimeOptions, loading: loadingSlotTime } =
    useFetchAllDataByID(GetSlotTimeById, slotTimeParams);

  /* -------------------- AUTO CHECKOUT DATE -------------------- */
  useEffect(() => {
    if (!values.checkin_date || !values.setting_id || !SlotTimeOptions) return;

    const selectedTime = SlotTimeOptions.find(
      opt => opt.setting_id === values.setting_id  // was opt.id — wrong field
    );

    if (!selectedTime?.hours) return;

    const hours = extractHoursFromDuration(selectedTime.hours);
    const checkout = formatDateForInput(
      calculateCheckoutDate(values.checkin_date, hours)
    );

    setValues(prev => ({ ...prev, checkout_date: checkout }));
    setErrors(prev => ({ ...prev, checkout_date: "" }));
  }, [values.checkin_date, values.setting_id, SlotTimeOptions]);

  /* -------------------- DURATION VALIDATION -------------------- */
  useEffect(() => {
    if (!values.checkin_date || !values.checkout_date || !values.setting_id || !SlotTimeOptions)
      return;

    const duration = calculateDurationInHours(values.checkin_date, values.checkout_date);
    const selectedTime = SlotTimeOptions.find(
      opt => opt.setting_id === values.setting_id
    );

    if (!selectedTime?.hours) return;

    const allowed = extractHoursFromDuration(selectedTime.hours);

    if (duration > allowed) {
      const corrected = formatDateForInput(
        calculateCheckoutDate(values.checkin_date, allowed)
      );

      setValues(prev => ({ ...prev, checkout_date: corrected }));
      setErrors(prev => ({
        ...prev,
        checkout_date: `Duration adjusted to ${allowed} hour(s)`
      }));

      setTimeout(() => {
        setErrors(prev => ({ ...prev, checkout_date: "" }));
      }, 3000);
    }
  }, [values.checkout_date]);

  /* -------------------- FORM SUBMIT -------------------- */
  const { loading: loadingSubmit, handleSubmit } = useFormSubmit(
    async (formValues) => {
      const payload = { ...formValues };
      return await Update({ id, data: payload });
    },
    (vals) => {
      const err = validateForm(vals, ParkingBookingRules);
      setErrors(err);
      return err;
    },
    () => navigate(-1),
    "Booking Updated successfully"
  );

  const isLoading =
    loadingSubmit ||
    loadingFetch ||
    loadingVehicle ||
    loadingBookingNumber ||
    loadingZone ||
    loadingSlotTime ||
    loadingSlotType ||
    loadingSetting;

  return (
    <>
      {isLoading && <Spinner isSpinner={true} />}

      <EditParkingBookingForm
        fields={formdata}
        values={values}
        errors={errors}
        handleChange={handleChange}
        handleSubmit={(e) => handleSubmit(e, values)}
        loading={loadingSubmit}
        title="Update Booking"
        BookingNumberOptions={[
          ...(BookingNumberOptions || []),
          ...(values.booking_number ? [{ booking_number: values.booking_number }] : [])
        ]}
        VehicleOptions={VehicleOptions}
        ZoneTypeOptions={ZoneTypeOptions}
        SlotTimeOptions={SlotTimeOptions}
        SlotTypeOptions={SlotTypeOptions}
        SettingOptions={SettingOptions}
        FIELD_DEPENDENCIES={FIELD_DEPENDENCIES}
      />
    </>
  );
};

export default EditParkingBooking;
